import { CardShell, PlayerPhoto, TeamBadgeSlot, TitleBar } from './card-chrome';
import { useArticlePredictedPoints } from '@hooks/useArticlePredictedPoints';
import {
  clampPredictedPlayerCount,
  describePredictionWindow,
  describeRowFixtures,
  PREDICTED_STATS,
  predictedPointsTitle,
  resolvePredictedStat
} from '@lib/article-blocks/predicted-points';
import type {
  GameweekWindow,
  PredictedPlayerRow
} from '@lib/article-blocks/predicted-points';
import type { SanityPredictedPointsBlock } from '@lib/article-blocks/types';

// Roughly the card at the box's player count, so the article does not jump
// when the predictions land.
const LOADING_ROW_HEIGHT_IN_REM = 4;
const LOADING_CHROME_HEIGHT_IN_REM = 7;

const PERCENT = 100;

const PredictedRow = ({
  row,
  rank,
  topValue,
  decimals,
  gameweekWindow
}: {
  row: PredictedPlayerRow;
  rank: number;
  topValue: number;
  decimals: number;
  gameweekWindow: GameweekWindow;
}) => {
  const { player, value, fixtures } = row;
  return (
    <li
      className="grid grid-cols-[1.25rem_auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl bg-[#2c3134] px-3 py-2 sm:px-4"
      data-cy="predicted-points-row"
    >
      <span className="text-center text-sm font-bold tabular-nums text-[#a5aaad]">
        {rank}
      </span>
      <PlayerPhoto player={player} sizeClass="h-10 w-10 sm:h-12 sm:w-12" />
      <div className="min-w-0">
        <div className="truncate text-base font-bold leading-tight text-[#fcfcfc] sm:text-lg">
          {player.displayName}
        </div>
        {/* On a phone the fixtures take their own line, as a double gameweek
            would otherwise be cut off. */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs leading-none text-[#a5aaad] sm:text-sm">
          <TeamBadgeSlot shortName={player.team.shortName} />
          <span>{player.position}</span>
          <span aria-hidden>·</span>
          <span className="tabular-nums">£{player.price.toFixed(1)}m</span>
          <span aria-hidden className="hidden sm:inline">
            ·
          </span>
          <span className="basis-full truncate sm:basis-auto">
            {describeRowFixtures(fixtures, gameweekWindow)}
          </span>
        </div>
        <div aria-hidden className="mt-2 h-1.5 rounded-full bg-[#3e4346]">
          <div
            className="h-full rounded-full bg-[#1FD0FF]"
            style={{ width: `${(value / topValue) * PERCENT}%` }}
          />
        </div>
      </div>
      <span className="text-xl font-bold tabular-nums text-[#fcfcfc] sm:text-2xl">
        {value.toFixed(decimals)}
      </span>
    </li>
  );
};

/**
 * Only members ever render this: the box is allowed in the premium body
 * alone. So a placeholder while loading shows no one numbers they may not
 * see, and a box that cannot be loaded leaves no trace.
 */
export const PredictedPointsBlock = ({
  value
}: {
  value: SanityPredictedPointsBlock;
}) => {
  const state = useArticlePredictedPoints(value);

  if (state.status === 'loading') {
    const rowCount = clampPredictedPlayerCount(value.playerCount);
    return (
      <div
        className="not-prose my-8 animate-pulse rounded-3xl bg-neutral-100"
        style={{
          minHeight: `${
            rowCount * LOADING_ROW_HEIGHT_IN_REM + LOADING_CHROME_HEIGHT_IN_REM
          }rem`
        }}
        data-cy="predicted-points-loading"
      />
    );
  }
  if (state.status === 'unavailable') return null;

  const { decimals } = PREDICTED_STATS[resolvePredictedStat(value.stat)];
  const topValue = state.rows[0].value;

  return (
    <CardShell testId="predicted-points">
      <TitleBar
        title={predictedPointsTitle(value)}
        detail={describePredictionWindow(state.gameweekWindow)}
      />
      <ol className="flex flex-col gap-2">
        {state.rows.map((row, index) => (
          <PredictedRow
            key={row.player.id}
            row={row}
            rank={index + 1}
            topValue={topValue}
            decimals={decimals}
            gameweekWindow={state.gameweekWindow}
          />
        ))}
      </ol>
    </CardShell>
  );
};
